import { store } from '../store/stencil-redux';
import { MapLayerState } from "../store/map/map.reducer";
import { MapLayer } from "./maps/maps-base-layer";
import { getAppCollection, getAppFeatureIsEnabled, getAppId, getAppMapLayer } from "../store/selectors";
import AppDatabase from "./db/app-db.service";
import { MapDataLayer } from "./maps/maps-data-layer";
import { CoreoCollection, CoreoDataStyle, CoreoMapLayer } from "../types";
import { MapRasterLayer } from "./maps/maps-raster-layer";
import { MapRasterTileLayer } from "./maps/maps-raster-tile-layer";
import { MapGeoJSONLayer } from "./maps/maps-geojson-layer";
import { MapCustomLayer } from "./maps/maps-custom-layer";
import { StaticMapLayer } from "./maps/maps-static-layer";
import { StaticLayersRegistry } from "./map-layer-static-registry.service";
import { Feature } from "geojson";

/** GeoJSON properties from `items.findProjectMapFeatures` (used for collection map labels). */
type CollectionMapFeatureProps = {
  key?: unknown;
  value?: unknown;
  data?: unknown;
  id?: unknown;
  icon?: unknown;
  color?: unknown;
};

const boundsToCoordinates = (bounds: number[]): number[][] => {
  const sw = [bounds[0], bounds[1]];
  const ne = [bounds[2], bounds[3]];

  return [
    [sw[0], ne[1]],
    [ne[0], ne[1]],
    [ne[0], sw[1]],
    [sw[0], sw[1]]
  ];
};

export class MapLayerFactory {

  private constructor() { }

  public static instance: MapLayerFactory = new MapLayerFactory();

  private getCollectionFeatures(collectionId: number, projectId: number) {
    return AppDatabase.instance.items.findProjectMapFeatures(
      q => q.where('collectionId = ?', collectionId).where('projectId = ?', projectId),
      { parseItemData: false }
    );
  }

  private readCollectionLabelConfig(collection: CoreoCollection | undefined): {
    labelAttributeId?: number | null;
    labelSourceType?: CoreoCollection['labelSourceType'];
  } {
    const style = collection?.style as
      | (CoreoDataStyle & { labelSourceType?: CoreoCollection['labelSourceType'] })
      | undefined;
    return {
      labelAttributeId: collection?.labelAttributeId ?? style?.labelAttributeId ?? null,
      labelSourceType:
        collection?.labelSourceType ??
        style?.labelSourceType ??
        style?.labelCollectionSourceType ??
        null
    };
  }

  private resolveCollectionLabelValue(
    props: CollectionMapFeatureProps,
    labelSourceType: CoreoCollection['labelSourceType'] | null | undefined,
    labelAttributePath: string | null
  ): string {
    if (labelSourceType === null) {
      return '';
    }
    if (labelSourceType === 'key') {
      return props.key != null ? String(props.key) : '';
    }
    if (labelSourceType === 'attribute' && labelAttributePath) {
      let data = props.data;
      if (typeof data === 'string') {
        try {
          data = data ? JSON.parse(data) : null;
        } catch {
          data = null;
        }
      }
      if (data && typeof data === 'object' && labelAttributePath in data) {
        const v = (data as Record<string, unknown>)[labelAttributePath];
        return v != null ? String(v) : '';
      }
      return '';
    }
    return props.value != null ? String(props.value) : '';
  }

  private async withCollectionLabels(
    features: Feature[],
    collection: CoreoCollection | undefined,
    projectId: number
  ): Promise<Feature[]> {
    const { labelAttributeId, labelSourceType: configuredSourceType } = this.readCollectionLabelConfig(collection);
    const labelSourceType = getAppFeatureIsEnabled('dataDrivenStyling')(store.getState())
      ? configuredSourceType
      : null;

    let labelAttributePath: string | null = null;
    if (labelSourceType === 'attribute' && typeof labelAttributeId === 'number') {
      const attr = await AppDatabase.instance.attributes.findById(labelAttributeId);
      labelAttributePath = attr?.path ?? null;
    }

    return features.map((feature) => {
      const properties = feature.properties as CollectionMapFeatureProps;
      const label = this.resolveCollectionLabelValue(properties, labelSourceType, labelAttributePath);
      const isPoint =
        feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint';
      const __icon =
        isPoint && properties.icon ? `${projectId}_${properties.id}` : undefined;

      return {
        ...feature,
        properties: {
          ...properties,
          __label: label ?? '',
          __icon,
          __color: properties?.color
        }
      };
    });
  }

  private async createCollectionLayer(layerState: MapLayerState): Promise<MapLayer> {
    const state = store.getState();
    const projectId = getAppId(state);

    const collection = getAppCollection(layerState.sourceId)(state);
    const baseFeatures = await this.getCollectionFeatures(layerState.sourceId, projectId);
    const features = await this.withCollectionLabels(baseFeatures, collection, projectId);
    const mapLayer = new MapDataLayer();
    await mapLayer.setFeatures(features);
    mapLayer.add(collection.id, collection.mapSort, collection.style);
    return mapLayer;
  }

  private async createCustomImageLayer(layerConfig: CoreoMapLayer, projectId: number): Promise<MapLayer> {
    const coordinates = layerConfig.layout ||
      (layerConfig.bounds ? boundsToCoordinates(layerConfig.bounds) : undefined);
    return new MapRasterLayer(layerConfig.source, coordinates, projectId);
  };

  private createRasterTileLayer(layerConfig: CoreoMapLayer, projectId: number): MapLayer {
    return new MapRasterTileLayer(
      layerConfig.source,
      layerConfig.layout,
      layerConfig.bounds,
      projectId,
      layerConfig.id
    );
  }

  async createCustomLayer(layerConfig: CoreoMapLayer): Promise<MapLayer> {
    const state = store.getState();
    const projectId = getAppId(state);

    if (layerConfig.sourceType === 'image') {
      return MapLayerFactory.instance.createCustomImageLayer(layerConfig, projectId);
    }
    if (layerConfig.sourceType === 'geojson') {
      return new MapGeoJSONLayer(layerConfig, projectId);
    }
    if (layerConfig.sourceType === 'raster-tiles') {
      return MapLayerFactory.instance.createRasterTileLayer(layerConfig, projectId);
    }
    const mapLayer = new MapCustomLayer(layerConfig);
    await mapLayer.init();
    return mapLayer;
  }

  async createMapLayer(layerState: MapLayerState): Promise<MapLayer> {
    const state = store.getState();
    switch (layerState.layerType) {
      case 'collection': {
        return MapLayerFactory.instance.createCollectionLayer(layerState);
      }
      case 'custom': {
        const layerConfig: CoreoMapLayer = getAppMapLayer(layerState.sourceId)(state);
        return MapLayerFactory.instance.createCustomLayer(layerConfig);
      }
      case 'static': {
        const staticLayer = StaticLayersRegistry.instance.getLayerById(layerState.id);
        if (staticLayer) {
          return new StaticMapLayer(staticLayer);
        }
        throw new Error(`Static layer not found: ${layerState.id}`);
      }
      default:
        throw new Error(`Unknown layer type: ${layerState.layerType}`);
    }
  }
}